import React, { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";

const VIDEO_SRC = "/Laptop_on_beach_sand_20261002182506.mp4";
const POSTER_START = "/hero-intro-start.jpg";
const POSTER_END = "/hero-intro-end.jpg"; // the "Nabil Hamid" frame

// Reduced-motion and data-saver visitors get the final frame as a still.
const prefersStill = () => {
  try {
    return (
      window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      Boolean(navigator.connection && navigator.connection.saveData)
    );
  } catch {
    return false;
  }
};

// "Overlap" hero: the one-shot intro (a beach-side CRT that resolves to
// "Nabil Hamid") fills the hero while the message is present from the first
// second, bottom-left on a corner-only scrim that stays clear of the name.
// On phones the video is a band above the text, so nothing covers the name.
const Hero = ({ setCurrentPage }) => {
  const videoRef = useRef(null);
  const [still] = useState(prefersStill);
  const [state, setState] = useState(still ? "ended" : "playing"); // playing | ended | blocked

  useEffect(() => {
    const video = videoRef.current;
    // React doesn't reliably reflect `muted` to the DOM; autoplay requires it.
    video.muted = true;
    if (still) return;
    video.play().catch(() => setState("blocked"));
  }, [still]);

  const replay = () => {
    const video = videoRef.current;
    video.currentTime = 0;
    video.play().catch(() => setState("blocked"));
  };

  const showReplay = state !== "playing";

  return (
    <section className="relative overflow-hidden bg-slate-950 lg:min-h-[calc(100dvh-4rem)]">
      <div className="relative aspect-video w-full lg:absolute lg:inset-0 lg:aspect-auto">
        <video
          ref={videoRef}
          src={VIDEO_SRC}
          poster={still ? POSTER_END : POSTER_START}
          muted
          playsInline
          preload={still ? "none" : "auto"}
          onPlay={() => setState("playing")}
          onEnded={() => setState("ended")}
          aria-label="Intro: a retro computer on a beach displays the name Nabil Hamid"
          className="h-full w-full object-cover"
        />
        {/* Corner scrim: the beach is bright from the first frame, so the text needs it from the start */}
        <div
          aria-hidden="true"
          className="absolute inset-0 hidden bg-[radial-gradient(ellipse_70%_75%_at_0%_100%,rgba(2,6,23,0.92)_0%,rgba(2,6,23,0.8)_50%,rgba(2,6,23,0)_100%)] lg:block"
        />
        <button
          type="button"
          onClick={replay}
          tabIndex={showReplay ? 0 : -1}
          aria-hidden={!showReplay}
          className={`absolute right-4 top-4 flex items-center gap-2 rounded-full bg-slate-950/70 px-3 py-2 text-sm text-slate-100 transition-opacity duration-200 ease-out hover:bg-slate-950/90 lg:right-8 lg:top-6 ${
            showReplay ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <RotateCcw size={16} strokeWidth={1.75} />
          {state === "blocked" ? "Play intro" : "Replay"}
        </button>
      </div>

      <div className="page relative flex py-10 lg:min-h-[calc(100dvh-4rem)] lg:items-end lg:pb-14 lg:pt-0">
        <div className="max-w-[30rem] lg:[text-shadow:0_1px_14px_rgba(2,6,23,0.65)]">
          <h1
            className="hero-rise text-4xl font-semibold leading-[1.05] tracking-tight text-slate-50 md:text-5xl lg:text-[2.75rem]"
            style={{ "--i": 3 }}
          >
            I build software, travel, and review films.
          </h1>
          <p
            className="hero-rise mt-5 max-w-[44ch] text-lg leading-relaxed text-slate-200"
            style={{ "--i": 4 }}
          >
            Projects I've shipped, photos from the road, and honest takes on
            movies you may have missed.
          </p>
          <div className="hero-rise mt-8 flex flex-wrap gap-3" style={{ "--i": 5 }}>
            <button type="button" onClick={() => setCurrentPage("reviews")} className="btn-primary">
              Read reviews
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage("projects")}
              className="btn-secondary [text-shadow:none]"
            >
              See projects
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
